import { Component, OnInit } from '@angular/core';
import { take, catchError } from 'rxjs/operators';
import { Product } from 'src/app/order/models/product.model';
import { ProductService } from 'src/app/quoting/services/product.service';
import { Warehouse } from '../../models/warehouse.model';
import { WarehouseLocation } from '../../models/warehouse-location.model';
import { SlowMovingRow, SlowMovingResult } from '../../models/warehouse-reporting.model';
import { WarehouseService } from '../../services/warehouse.service';
import { WarehouseLocationService } from '../../services/warehouse-location.service';
import { StockQuantityService } from '../../services/stock-quantity.service';
import { ReportCsvService } from '../../services/report-csv.service';
import { WarehouseReportingService } from '../../services/warehouse-reporting.service';
import moment from 'moment';

/**
 * Stock sitting with no movement in a while — the aging / dead-stock question.
 *
 * Every row returned is already past the threshold: the backend does the filtering, against
 * `lastMovementAt` on the same holdings every other stock report replays from the ledger. There
 * is nothing enforced here and nothing this screen fixes — it names stock worth a decision
 * (write it down, move it, discount it) and leaves the decision to a person, the same posture
 * the batch and reconciliation reports take.
 */
@Component({
    selector: 'app-report-slow-moving',
    templateUrl: './report-slow-moving.component.html',
    styleUrls: ['./report-slow-moving.component.scss'],
    standalone: false
})
export class ReportSlowMovingComponent implements OnInit {

  warehouses: Warehouse[] = [];
  locations: WarehouseLocation[] = [];
  products: Product[] = [];

  selectedWarehouseId: number = -1;
  selectedLocationId: number = -1;
  selectedProductId: number = -1;

  /** Days with no real movement before a holding counts as slow-moving. Matches the backend default. */
  days: number = 90;

  result: SlowMovingResult = new SlowMovingResult();

  hasRun: boolean = false;
  running: boolean = false;

  loadFailed: boolean = false;
  loadError: string = '';

  constructor(private warehouseReportingService: WarehouseReportingService,
    private warehouseService: WarehouseService,
    private warehouseLocationService: WarehouseLocationService,
    private productService: ProductService,
    private stockQuantityService: StockQuantityService,
    private reportCsvService: ReportCsvService) { }

  ngOnInit(): void {
    this.warehouseService.getAllWarehouses()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load warehouses');
      return e;
    }))
    .subscribe((data: Warehouse[]) => {
      this.warehouses = data;
    })

    this.productService.getAllActiveProducts()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load products');
      return e;
    }))
    .subscribe((data: Product[]) => {
      this.products = data;
    })
  }

  /** Locations are scoped by warehouseId, so the location list only means anything once a warehouse is chosen. */
  onWarehouseChanged(warehouseId: any) {
    this.selectedWarehouseId = +warehouseId;
    this.selectedLocationId = -1;
    this.locations = [];

    if (this.selectedWarehouseId === -1) {
      return;
    }

    this.warehouseLocationService.getWarehouseLocationsForWarehouse(this.selectedWarehouseId)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load warehouse locations');
      return e;
    }))
    .subscribe((data: WarehouseLocation[]) => {
      this.locations = data;
    })
  }

  get daysInvalid(): boolean {
    return this.days === null || this.days === undefined || !Number.isInteger(+this.days) || +this.days < 0;
  }

  loadReport() {
    if (this.daysInvalid) {
      return;
    }

    this.running = true;
    this.loadFailed = false;
    this.loadError = '';

    this.warehouseReportingService.getSlowMovingStock(this.requestFilters())
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      this.running = false;
      this.loadFailed = true;
      this.loadError = e && e.status ? 'The server answered ' + e.status + '.' : '';
      this.result = new SlowMovingResult();
      this.hasRun = false;
      return e;
    }))
    .subscribe((result: SlowMovingResult) => {
      this.result = result ? result : new SlowMovingResult();
      this.hasRun = true;
      this.running = false;
    })
  }

  private requestFilters(): any {
    return {
      warehouseId: this.selectedWarehouseId,
      warehouseLocationId: this.selectedLocationId,
      productId: this.selectedProductId,
      days: +this.days,
    };
  }

  /** Never moved at all. The backend sorts these first — they are the most stale, not a lesser case. */
  neverMoved(row: SlowMovingRow): boolean {
    return row.daysSinceLastMovement === null || row.daysSinceLastMovement === undefined;
  }

  formatQty(qty: number): string {
    return this.stockQuantityService.round(qty).toString();
  }

  formatDateTime(value: string): string {
    return value ? moment(value).format('DD/MM/YYYY HH:mm:ss') : '';
  }

  exportToCSV() {
    const headers: string[] = ['Product', 'SKU', 'Warehouse', 'Aisle', 'Shelf',
      'Qty', 'Last Movement', 'Days Since Last Movement'];

    const rows: any[][] = this.result.rows.map((row: SlowMovingRow) => [
      row.productName,
      row.sku,
      row.warehouseName,
      row.aisle,
      row.shelf,
      this.formatQty(row.ledgerQty),
      this.formatDateTime(row.lastMovementAt),
      this.neverMoved(row) ? 'Never moved' : row.daysSinceLastMovement
    ]);

    this.reportCsvService.download('slow-moving-stock', headers, rows);
  }
}
